<script setup lang="ts">
import { eventBus } from "#bb/lib/eventBus";
import {
  CandidateAssessmentBlocksKey,
  collectAnswerableAssessmentBlocks,
} from "#bb/lib/assessmentAnswers";
import { ResourcePreloadKey } from "#bb/lib/resourcePreload";
import { isResourceBuildingBlockDebugEnabled } from "#bb/lib/buildingBlockDebug";

const logger = useLogger("ResourceRenderer");

// The candidate floating-input chat self-gates on the context agent file.
const { site } = useSite();
const currentSiteId = computed(() => site.value?.id as string | undefined);
const { configuration: loadingIndicator } = useCandidateLoadingIndicator();

// Track whether the floating chat button is visible so we can add bottom
// padding to prevent it from overlapping content when scrolled to the bottom.
const chatAgentPresent = ref(false);

interface Props {
  resourcePath: string;
  mode?: "viewer" | "editor"; // or a boolean if you prefer
  /**
   * Embedded render (e.g. the in-chat assessment report): suppresses the candidate floating chat and
   * its bottom padding so a resource can be rendered inside another surface without a nested chat.
   */
  embedded?: boolean;
}
const props = defineProps<Props>();

const loading = ref(false);
const error = ref(null);
const definitionPath = ref(props.resourcePath);
const route = useRoute();
let loadSequence = 0;

// The report page identifies its subject via `?user=<profileId>`; pass it to the chat so
// report tools read that candidate's data instead of the signed-in viewer's.
const chatSubjectUserId = computed(() => {
  const user = route.query.user;
  const value = Array.isArray(user) ? user[0] : user;
  return typeof value === "string" && value.trim() ? value : undefined;
});

const resourceKey = ref(props.resourcePath);
const renderFeatures = reactive({
  editor: props.mode === "editor",
  resourceKey: resourceKey.value,
  resourceDefinitionPath: definitionPath.value,
  debugBuildingBlocks: false,
});
provide("render-features", renderFeatures);
logger.debug("Provided render-features", renderFeatures);

// Access content manager bound to resource key
let { contentState, getResolvedDataPath } = useContentManager(resourceKey.value);
const { metadata, setMetadata } = useResourceMetadata();
watch(
  metadata,
  (value) => {
    renderFeatures.debugBuildingBlocks =
      isResourceBuildingBlockDebugEnabled(value);
  },
  { immediate: true },
);
const { fetchAnswers } = useAnswers();
const { setDefinition } = useResourceDefinition();
const resourceLoader = useCandidateResourceLoader();
const conditionValues = useConditionValues(resourceKey);
const conditionalUserData = useConditionalUserData(resourceKey);
provide(ResourcePreloadKey, {
  preload: resourceLoader.preload,
  isReady: resourceLoader.isReady,
  loadConditions: conditionalUserData.load,
});
const answerableAssessmentBlocks = computed(() => {
  const activeResourceKey = resourceKey.value;
  return collectAnswerableAssessmentBlocks(contentState.value, (id) =>
    activeResourceKey ? getResolvedDataPath(id).join("/") : ""
  );
});
provide(CandidateAssessmentBlocksKey, answerableAssessmentBlocks);

function bindContentManager(path: string) {
  resourceKey.value = path;
  renderFeatures.resourceKey = path;
  ({ contentState, getResolvedDataPath } = useContentManager(path));
  conditionValues.clearComponentStates();
}

function refreshAnswersInBackground(path: string) {
  if (!import.meta.client) {
    return;
  }

  void fetchAnswers({ force: true }).then(() => {
    logger.info("Answers refreshed in background", { path });
  });
}

async function fetchResourceDefinition(
  path: string,
  sequence: number,
  refreshDefinition = false,
  fatalOnError = true,
) {
  logger.info("Starting fetchResourceDefinition for path:", path);
  try {
    if (fatalOnError) {
      loading.value = true;
    }
    logger.debug("Emitting event: resource:beforeLoad", { path });
    eventBus.emit("resource:beforeLoad", { path });

    let result = await resourceLoader.load(path, { refreshDefinition });
    if (sequence !== loadSequence) return;

    if (
      import.meta.client
      && !refreshDefinition
      && result.redirectHref
    ) {
      result = await resourceLoader.load(path, {
        refreshDefinition: true,
      });
      if (sequence !== loadSequence) return;
    }

    if (await resourceLoader.activateRedirect(result)) {
      return;
    }

    definitionPath.value = result.definitionPath;
    renderFeatures.resourceDefinitionPath = result.definitionPath;
    setDefinition(result.raw);
    setMetadata(result.metadata);
    loading.value = false;
    eventBus.emit("resource:loadData", { path: result.definitionPath });
    eventBus.emit("resource:afterLoad", { path: result.definitionPath });
    if (!result.hasConditionalAnswers) {
      refreshAnswersInBackground(result.definitionPath);
    }
    logger.info("Completed fetchResourceDefinition for path:", path);
  } catch (err) {
    if (sequence !== loadSequence) return;
    if (!fatalOnError) {
      logger.warn("Unable to refresh resource definition; keeping cached content", {
        path,
        error: err,
      });
      return;
    }
    logger.error("Error in fetchResourceDefinition for path:", path, err);
    throw createError({
      statusCode: 404,
      statusMessage: "Resource Not Found",
      fatal: true,
      data: {
        resource: path,
      },
    });
  } finally {
    if (sequence === loadSequence && loading.value) {
      loading.value = false;
      logger.info("Loading state set to false for path:", path);
    }
  }
}

async function activateResource(
  path: string,
  refreshDefinition = false,
  background = false,
) {
  const sequence = ++loadSequence;
  if (!background) {
    loading.value = true;
  }
  await nextTick();
  if (sequence !== loadSequence) return;
  if (!background) {
    bindContentManager(path);
  }
  await fetchResourceDefinition(
    path,
    sequence,
    refreshDefinition,
    !background,
  );
}

function refreshActiveResource() {
  if (loading.value) return;
  void activateResource(props.resourcePath, true, true);
}

watch(
  () => props.resourcePath,
  async (newPath, oldPath) => {
    if (newPath === oldPath) return;
    const wasCached = resourceLoader.isReady(newPath);
    await activateResource(newPath);
    if (wasCached) {
      refreshActiveResource();
    }
  }
);

if (import.meta.server) {
  await activateResource(props.resourcePath);
} else {
  bindContentManager(props.resourcePath);

  if (resourceLoader.isReady(props.resourcePath)) {
    await activateResource(props.resourcePath);
    onMounted(refreshActiveResource);
  } else {
    loading.value = true;
    onMounted(() => {
      if (loadSequence === 0) {
        void activateResource(props.resourcePath);
      }
    });
  }

  const refreshOnFocus = () => {
    refreshActiveResource();
  };
  onMounted(() => window.addEventListener("focus", refreshOnFocus));
  onBeforeUnmount(() => window.removeEventListener("focus", refreshOnFocus));
}
</script>

<template>
  <!-- Display loading or error states if desired -->
  <div v-if="error">
    <p class="text-red-500">Error loading resource: {{ error }}</p>
  </div>
  <div v-else-if="loading">
    <LoadingIndicator
      v-if="loadingIndicator.visible"
      :title="loadingIndicator.title"
      :description="loadingIndicator.description"
      :icon="loadingIndicator.icon"
      :tile="loadingIndicator.tile"
      :spin="loadingIndicator.spin"
      :pulse="loadingIndicator.pulse"
      :ping="loadingIndicator.ping"
      class="py-24 sm:py-32"
    />
  </div>
  <div v-else :class="{ 'pb-32': chatAgentPresent && !embedded }">
    <!-- @container establishes the responsive query context: the building
         blocks' tablet:/desktop: variants resolve against THIS element's width
         (the render width), not the browser viewport. The floating ChatExperience
         is kept OUTSIDE this wrapper so its fixed positioning stays anchored to
         the viewport rather than being trapped by the container's containing block. -->
    <div :key="resourceKey" class="@container">
      <renderer v-for="(block, idx) in contentState" :key="block.id || idx" :id="block.id" />
    </div>
    <!-- Candidate floating input: only appears when an agent governs this context. Suppressed in
         embedded mode (e.g. the in-chat report) so the report never mounts a nested chat surface. -->
    <ChatExperience
      v-if="!embedded"
      surface="candidate"
      presentation="responsive"
      :site-id="currentSiteId"
      :context-path="definitionPath"
      :subject-user-id="chatSubjectUserId"
      @agent-resolved="(agent) => chatAgentPresent = !!agent"
    >
      <!-- In-chat report: rendered only when the governing agent opts into `completion: "report"`.
           The candidate owns its report UI; the chat module just exposes runId/reportFile here. -->
      <template #report="{ completion: reportCompletion }">
        <CandidateAssessmentReport :completion="reportCompletion" />
      </template>
    </ChatExperience>
  </div>
</template>
